import React, { useState } from 'react';
import { FaLock, FaUser, FaKey, FaShieldAlt, FaArrowRight, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { AUTH_STORAGE_KEY, USERS_STORAGE_KEY, CURRENT_USER_KEY, SESSION_KEY } from './billTrackerData';

const BillTrackerAuth = ({ onAuthenticated }) => {
  const [activeTab, setActiveTab] = useState('login'); // 'login' or 'setup'
  
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Setup credentials state
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Helper to load all registered users
  const getAllUsers = () => {
    let users = {};
    try {
      const stored = localStorage.getItem(USERS_STORAGE_KEY);
      if (stored) {
        users = JSON.parse(stored);
      }
    } catch (e) {
      console.error(e);
    }

    // Check if legacy single user exists and migrate
    try {
      const legacy = localStorage.getItem(AUTH_STORAGE_KEY);
      if (legacy) {
        const parsedLegacy = JSON.parse(legacy);
        if (parsedLegacy && parsedLegacy.username) {
          users[parsedLegacy.username.toLowerCase()] = parsedLegacy.password;
        }
      }
    } catch (e) {}

    // Ensure default master user 'adedeji' exists
    if (!users['adedeji']) {
      users['adedeji'] = 'password123';
    }

    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
    return users;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    const users = getAllUsers();
    const cleanUser = username.trim().toLowerCase();

    if (users[cleanUser] && users[cleanUser] === password) {
      localStorage.setItem(SESSION_KEY, 'true');
      localStorage.setItem(CURRENT_USER_KEY, cleanUser);
      onAuthenticated(cleanUser);
    } else {
      setError('Invalid username or password. If you are new, click "Create New Account" below.');
    }
  };

  const handleSetup = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const cleanUser = newUsername.trim().toLowerCase();
    if (!cleanUser || !newPassword.trim()) {
      setError('Username and password are required.');
      return;
    }
    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const users = getAllUsers();
    const isNew = !users[cleanUser];
    users[cleanUser] = newPassword;
    localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));

    // Also update legacy key if it's adedeji
    if (cleanUser === 'adedeji') {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ username: cleanUser, password: newPassword }));
    }

    setSuccess(isNew ? 'Account created! Logging you in to your private workspace...' : 'Password updated! Logging in...');

    setTimeout(() => {
      localStorage.setItem(SESSION_KEY, 'true');
      localStorage.setItem(CURRENT_USER_KEY, cleanUser);
      onAuthenticated(cleanUser);
    }, 1000);
  };

  return (
    <div className="bt-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
      <div className="bt-auth-card">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '24px',
            marginBottom: '16px',
            boxShadow: '0 8px 20px rgba(79, 70, 229, 0.3)'
          }}>
            <FaLock />
          </div>
          <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '800' }}>
            Bill Tracker Access
          </h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.88rem' }}>
            Enter your credentials to manage your confidential cashflow ledger
          </p>
        </div>

        {/* Tabs: Sign In / Create or Reset Credentials */}
        <div className="bt-auth-tabs">
          <button
            type="button"
            className={`bt-auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => { setActiveTab('login'); setError(''); setSuccess(''); }}
          >
            Sign In
          </button>
          <button
            type="button"
            className={`bt-auth-tab ${activeTab === 'setup' ? 'active' : ''}`}
            onClick={() => { setActiveTab('setup'); setError(''); setSuccess(''); }}
          >
            Create / Reset Password
          </button>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FaExclamationCircle /> {error}
          </div>
        )}

        {success && (
          <div style={{
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            color: '#059669',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FaCheckCircle /> {success}
          </div>
        )}

        {activeTab === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="bt-form-group">
              <label>Username</label>
              <div style={{ position: 'relative' }}>
                <FaUser style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                <input
                  type="text"
                  className="bt-input"
                  style={{ paddingLeft: '36px' }}
                  placeholder="e.g. adedeji"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="bt-form-group">
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <FaKey style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                <input
                  type="password"
                  className="bt-input"
                  style={{ paddingLeft: '36px' }}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="bt-btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px', padding: '12px' }}
            >
              Unlock Dashboard <FaArrowRight />
            </button>

            <div style={{
              marginTop: '16px',
              padding: '10px',
              borderRadius: '8px',
              backgroundColor: '#f8fafc',
              border: '1px dashed #cbd5e1',
              fontSize: '0.78rem',
              color: '#64748b',
              textAlign: 'center'
            }}>
              Initial default access: <strong>adedeji</strong> / <strong>password123</strong> (or use the second tab to customize anytime)
            </div>
          </form>
        ) : (
          <form onSubmit={handleSetup}>
            <div className="bt-form-group">
              <label>Choose Username</label>
              <div style={{ position: 'relative' }}>
                <FaUser style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                <input
                  type="text"
                  className="bt-input"
                  style={{ paddingLeft: '36px' }}
                  placeholder="Your preferred username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="bt-form-group">
              <label>New Password</label>
              <div style={{ position: 'relative' }}>
                <FaKey style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                <input
                  type="password"
                  className="bt-input"
                  style={{ paddingLeft: '36px' }}
                  placeholder="At least 4 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="bt-form-group">
              <label>Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <FaShieldAlt style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                <input
                  type="password"
                  className="bt-input"
                  style={{ paddingLeft: '36px' }}
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="bt-btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px', padding: '12px' }}
            >
              Save Credentials <FaCheckCircle />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BillTrackerAuth;
